import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Users, FileText, CheckCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-green-700 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 text-lg">Last updated: April 1, 2026</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6" /> Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              CashPro Financial Services recognizes the expectations of its customers with regard to privacy, confidentiality, and security of their personal information that resides with us. Keeping personal information of customers secure and using it solely for activities related to the Bank and preventing any misuse thereof is a top priority of CashPro Financial Services. This Privacy Policy (“Policy”) governs the way in which we collect, use, disclose, store, secure, and dispose of personal information and sensitive personal data or information.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6" /> Information We Collect
            </h2>
            <p className="text-gray-700 mb-3">We may collect the following types of information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Personal Information:</strong> Name, date of birth, PAN, Aadhaar, contact details, address, email, phone number.</li>
              <li><strong>Financial Information:</strong> Income details, bank account information, credit history, loan details, repayment records.</li>
              <li><strong>Technical Information:</strong> IP address, browser type, device information, cookies, and usage data when you interact with our website.</li>
              <li><strong>Sensitive Personal Data:</strong> Biometric data (if applicable), passwords, financial passwords, and other sensitive information as defined under applicable laws.</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6" /> How We Use Your Information
            </h2>
            <p className="text-gray-700 mb-3">We use the information collected for:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Processing loan applications and providing financial services.</li>
              <li>Verifying your identity and eligibility.</li>
              <li>Communicating with you regarding your applications, transactions, and offers.</li>
              <li>Improving our website, products, and customer service.</li>
              <li>Complying with legal and regulatory requirements.</li>
              <li>Detecting and preventing fraud and unauthorized activities.</li>
            </ul>
          </section>

          {/* Information Sharing & Disclosure */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6" /> Information Sharing & Disclosure
            </h2>
            <p className="text-gray-700 mb-3">
              We do not sell or rent your personal information to third parties. However, we may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>With our partner banks, NBFCs, and financial institutions for loan processing and disbursal.</li>
              <li>With regulatory authorities, law enforcement, or as required by law.</li>
              <li>With service providers who assist us in operating our business (e.g., IT services, customer support).</li>
              <li>With your consent or at your direction.</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6" /> Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We implement industry‑standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These include encryption, firewalls, secure socket layer (SSL) technology, and restricted access controls. Our employees and partners are bound by confidentiality obligations.
            </p>
          </section>

          {/* Retention of Information */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4">Retention of Information</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Policy, unless a longer retention period is required or permitted by law. After the retention period, we securely dispose of your information.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6" /> Your Rights
            </h2>
            <p className="text-gray-700 mb-3">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Access and review your personal information held by us.</li>
              <li>Request correction or update of inaccurate information.</li>
              <li>Withdraw consent for processing (subject to legal obligations).</li>
              <li>Lodge a complaint with the relevant data protection authority.</li>
            </ul>
            <p className="text-gray-700 mt-3">
              To exercise these rights, please contact our Grievance Officer at <a href="mailto:grievance@cashpro.co.in" className="text-green-600 hover:underline">grievance@cashpro.co.in</a>.
            </p>
          </section>

          {/* Cookies & Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4">Cookies & Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website uses cookies and similar tracking technologies to enhance user experience, analyze site traffic, and personalize content. You can control cookie preferences through your browser settings.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-bold text-green-700 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this Policy periodically.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-green-50 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-green-700 mb-3">Contact Us</h2>
            <p className="text-gray-700 mb-2">
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>CashPro Financial Services</strong><br />
              2nd Floor, Bhawani Complex, Sigra Rathyatra Road,<br />
              Above SBI Bank, Varanasi – 221010, Uttar Pradesh</p>
              <p><strong>Email:</strong> <a href="mailto:info@cashpro.co.in" className="text-green-600 hover:underline">info@cashpro.co.in</a></p>
              <p><strong>Phone:</strong> <a href="tel:+918887141052" className="text-green-600 hover:underline">+91 88871 41052</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;